import { useEffect, useRef, useState, type ComponentType } from "react";

import type {
  EntityViewType,
  StaticEntityType,
  StaticLayerPayload,
} from "@config";
import { useViewContext } from "@editor-providers";
import { useCurrentStepIndex, useFirstStepHeight } from "../hooks";
import { StyledNarratorContainer, StyledStep } from "../styles/Narrator.styled";
import "../styles/index.scss";
import type {
  EditorDataProps,
  EditorSlotsProps,
  NarratorProps,
} from "../types";

export const NarratorView = ({
  entity,
  setChild,
  elementChild,
  elements,
  editorSlots,
  editorData,
}: {
  entity: EntityViewType;
  setChild: ComponentType<{ id: string } | StaticLayerPayload>;
  elementChild: ComponentType<{ id: string } | StaticLayerPayload>;
  elements: Record<string, any>;
  editorData?: EditorDataProps;
  editorSlots?: EditorSlotsProps;
}) => {
  const { view } = useViewContext();
  const isEditor = view === "editor";
  const isEntityType = checkIsEntityType(elements, view);
  const {
    settings,
    id,
    children: entityChildren,
  } = entity as StaticEntityType<NarratorProps>;
  const contentRef = useRef<HTMLDivElement | null>(null);
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const currentSetIndex = useCurrentStepIndex(contentRef);
  const firstStepHeight = useFirstStepHeight(contentRef, entityChildren);
  const narratorStyle = settings.style ?? "side-by-side";
  const isSideBySideOnMobile = narratorStyle === "side-by-side";
  const [isMediaStuck, setIsMediaStuck] = useState(false);

  useEffect(() => {
    if (!isSideBySideOnMobile || !stickySentinelRef.current) return;
    const sentinel = stickySentinelRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => setIsMediaStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isSideBySideOnMobile]);

  const narratorLayout =
    narratorStyle === "fullscreen-side-by-side" && settings.layout === "center"
      ? "left"
      : (settings.layout ?? "right");

  const StepOutline = editorSlots?.stepOutline;
  const StepActions = editorSlots?.stepActions;
  const AddStep = editorSlots?.addStep;
  const FullscreenArea = editorSlots?.fullscreenArea;
  const AddEntityPlaceholder = editorSlots?.addEntityPlaceholder;
  const Set = setChild;
  const Element = elementChild;

  const currentSetId =
    currentSetIndex != null
      ? isEntityType
        ? entityChildren[currentSetIndex].id
        : entityChildren[currentSetIndex]
      : "";

  return (
    <StyledNarratorContainer
      className="combo combo-narrator"
      $isForPreview={!isEditor}
      $pointsSpacing={settings.pointsSpacing}
      $firstStepHeight={firstStepHeight}
      data-narrator-layout={narratorLayout}
      data-narrator-style={narratorStyle}
    >
      <div className="com-narrator__split">
        {isSideBySideOnMobile && (
          <div
            ref={stickySentinelRef}
            className="combo-narrator__sticky-sentinel"
            aria-hidden
          />
        )}
        <div
          className="combo-narrator__content"
          ref={contentRef}
          style={{ gap: (settings.pointsSpacing ?? 350) + "px" }}
        >
          {entityChildren.map(
            (set: EntityViewType<StaticEntityType>, setIndex: number) => {
              const isEntity = typeof set !== "string";
              const setId = isEntity ? set.id : set;
              return (
                <StyledStep
                  key={setId}
                  onClick={(e) => {
                    if (!isEditor) return;
                    const target = e.target as HTMLElement;
                    if (
                      target.classList.contains(
                        "combo-narrator__content__step__add-step",
                      ) ||
                      target.closest(".combo-narrator__content__step__add-step")
                    ) {
                      return;
                    }
                    editorData?.scrollToStep();
                  }}
                  id={isEditor ? `narrator-editor-point-${setId}` : undefined}
                  className="combo-narrator__content__step"
                  data-step-index={setIndex}
                  $contentOpacity={currentSetIndex === setIndex ? 1 : 0.2}
                  $stepStyle={{
                    bgColor: settings.stepBackgroundColor ?? "#ffffff",
                    borderColor: settings.stepBorderColor,
                    borderWidth: settings.stepBorderWidth,
                    borderRadius: settings.stepBorderRadius,
                    shadow: settings.stepShadow,
                  }}
                >
                  {setIndex === 0 && AddStep && (
                    <AddStep index={setIndex} position="top" id={id} />
                  )}
                  {isEditor && StepActions && (
                    <StepActions
                      setId={setId}
                      isActive={currentSetIndex === setIndex}
                    />
                  )}
                  {isEditor && (
                    <button
                      type="button"
                      aria-label={`Activate step ${setIndex + 1}`}
                      className="combo-narrator__content__step__click-catcher"
                      data-layer-id={setId}
                      data-layer-type="narrator-step"
                      data-disabled-context
                    />
                  )}
                  {isEntity ? (
                    <Set
                      entity={
                        {
                          ...set,
                          layers: {
                            ...(entity.layers || {}),
                            combo: { settings, index: setIndex },
                          },
                        } as StaticEntityType
                      }
                    />
                  ) : (
                    <Set id={set} />
                  )}
                  {AddStep && (
                    <AddStep index={setIndex} position="bottom" id={id} />
                  )}
                </StyledStep>
              );
            },
          )}
        </div>

        {narratorStyle === "fullscreen" && FullscreenArea && (
          <FullscreenArea id={id} />
        )}
        {StepOutline && (
          <StepOutline currentStepIndex={currentSetIndex} id={id} />
        )}
        <div
          className="combo-narrator__media-container"
          data-narrator-style={narratorStyle}
          data-stuck={isSideBySideOnMobile && isMediaStuck ? "true" : undefined}
        >
          <div
            className="combo-narrator__media"
            data-is-last-step-active={
              currentSetIndex === entityChildren.length - 1 ? "true" : "false"
            }
          >
            {currentSetId &&
              Object.keys(elements).map((setKey: string, index: number) => {
                const match = setKey.match(/set-(.*?)-group-(.*)/);
                const groupId = match ? match[2] : "";
                const media = elements[setKey];
                return (
                  <div
                    className="combo-narrator__media__wrapper"
                    data-narrator-step-active={
                      currentSetIndex === index ? "true" : "false"
                    }
                    data-layer-id={groupId}
                    data-layer-type="group"
                    key={index}
                    style={{
                      pointerEvents: "none",
                      zIndex: currentSetIndex === index ? 10 : 0,
                      opacity: currentSetIndex === index ? 1 : 0,
                    }}
                  >
                    {media && media[0] ? (
                      isEntityType ? (
                        <Element entity={media[0] as StaticEntityType} />
                      ) : (
                        <Element id={media[0] as unknown as string} />
                      )
                    ) : (
                      AddEntityPlaceholder && (
                        <AddEntityPlaceholder parentId={groupId} />
                      )
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </StyledNarratorContainer>
  );
};

const checkIsEntityType = (elements: Record<string, any>, view: string) => {
  const element = Object.values(elements)[0];
  if (!element) return false;
  return !element[0]
    ? !["editor", "thumbnail"].includes(view)
    : typeof element[0] !== "string";
};
